import React, { useState, useEffect, useRef } from 'react';
import BaseChart from './BaseChart';
import { toast } from 'react-toastify';
import tokenService from '../../services/tokenService';
import chartSyncActivator from '../../utils/ChartSyncActivator';
import { ensureValidDate, getDateRangeFromLocalStorage, formatChartTimeLabel, validateDateRange } from '../../utils/DateUtils';

// Активируем синхронизацию графиков при импорте компонента
if (!chartSyncActivator.initialized) {
  chartSyncActivator.initialize();
}

const EngineChart = ({ vehicle, startDate: propsStartDate, endDate: propsEndDate }) => {
  const [startDate, setStartDate] = useState(propsStartDate || null);
  const [endDate, setEndDate] = useState(propsEndDate || null);
  const abortControllerRef = useRef(null);
  
  // Обновляем локальное состояние при изменении свойств
  useEffect(() => {
    if (propsStartDate) {
      const newStartDate = ensureValidDate(propsStartDate);
      if (!startDate || Math.abs(newStartDate.getTime() - startDate.getTime()) > 1000) {
        console.log('EngineChart: Обновление startDate из props:', newStartDate.toISOString());
        setStartDate(newStartDate);
      }
    }
    if (propsEndDate) {
      const newEndDate = ensureValidDate(propsEndDate);
      if (!endDate || Math.abs(newEndDate.getTime() - endDate.getTime()) > 1000) {
        console.log('EngineChart: Обновление endDate из props:', newEndDate.toISOString());
        setEndDate(newEndDate);
      }
    }
  }, [propsStartDate, propsEndDate, startDate, endDate]);

  // Функция для форматирования метки времени с использованием общей утилиты
  const formatTimeLabel = (date) => {
    return formatChartTimeLabel(date, startDate, endDate);
  };

  // Кастомные опции для графика состояния двигателя
  const getChartOptions = () => {
    return {
      scales: {
        y: {
          min: 0,
          max: 1150,
          ticks: {
            stepSize: 500,
            callback: function(value) {
              if (value === 0) return "Выключен";
              if (value === 1000) return "Включен";
              return "";
            }
          },
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.05)'
          }
        }
      }
    };
  };

  // Обновляем обработчик события изменения диапазона дат
  const handleDateRangeChanged = (event) => {
    try {
      const { startDate: newStartDate, endDate: newEndDate, period, timestamp, forceUpdate } = event.detail;
      
      console.log('EngineChart: Обработка события изменения диапазона дат:', event.detail);
      
      // Сохраняем метку времени последнего обновления
      window.lastDateUpdateTime = timestamp || new Date().getTime();
      
      // Если передан предустановленный период (неделя, месяц и т.д.)
      if (period) {
        console.log('EngineChart: Обработка предустановленного периода:', period);
        
        // Создаем даты на основе предустановленного периода
        let validStartDate = new Date();
        let validEndDate = new Date();
        
        switch(period) {
          case 'day':
            // Текущий день
            validStartDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            // Последние 7 дней
            validStartDate = new Date(validEndDate);
            validStartDate.setDate(validEndDate.getDate() - 7);
            validStartDate.setHours(0, 0, 0, 0);
            break;
          case 'month':
            // Последние 30 дней
            validStartDate = new Date(validEndDate);
            validStartDate.setDate(validEndDate.getDate() - 30);
            break;
          case 'year':
            // Последние 365 дней
            validStartDate = new Date(validEndDate);
            validStartDate.setDate(validEndDate.getDate() - 365);
            break;
          default:
            // По умолчанию - неделя
            validStartDate = new Date(validEndDate);
            validStartDate.setDate(validEndDate.getDate() - 7);
        }
        
        setStartDate(validStartDate);
        setEndDate(validEndDate);
        return;
      }
      
      // Проверяем наличие дат
      if (newStartDate && newEndDate) {
        // Преобразуем строковые даты в объекты Date используя общую утилиту
        const validStartDate = ensureValidDate(newStartDate);
        const validEndDate = ensureValidDate(newEndDate);
        
        console.log('EngineChart: Преобразованные даты:', {
          startDate: validStartDate.toISOString(),
          endDate: validEndDate.toISOString()
        });
        
        setStartDate(validStartDate);
        setEndDate(validEndDate);
      } else if (forceUpdate) {
        // Получаем даты из localStorage при forceUpdate используя общую утилиту
        const { startDate: storedStartDate, endDate: storedEndDate } = getDateRangeFromLocalStorage();
        
        if (storedStartDate && storedEndDate) {
          console.log('EngineChart: Даты из localStorage:', {
            startDate: storedStartDate.toISOString(),
            endDate: storedEndDate.toISOString()
          });
          
          setStartDate(storedStartDate);
          setEndDate(storedEndDate);
        }
      } else {
        console.warn('EngineChart: Получены неполные данные в событии dateRangeChanged:', event.detail);
      }
    } catch (error) {
      console.error('EngineChart: Ошибка при обработке события изменения дат:', error);
    }
  };

  // Функция принудительного обновления данных при смене транспортного средства
  const handleForceUpdate = (event) => {
    if (!event.detail) return;
    
    const { vehicleId } = event.detail;
    
    if (vehicle && vehicle.id === vehicleId) {
      console.log(`EngineChart: Принудительное обновление данных для ТС ${vehicleId}`);
      // Не вызываем fetchEngineData напрямую - BaseChart управляет загрузкой через useChartState
      // fetchEngineData будет вызван автоматически при изменении данных
    }
  };

  // Обрабатываем события изменения дат и смены транспортного средства
  useEffect(() => {
    document.addEventListener('dateRangeChanged', handleDateRangeChanged);
    document.addEventListener('forceVehicleUpdate', handleForceUpdate);
    
    return () => {
      document.removeEventListener('dateRangeChanged', handleDateRangeChanged);
      document.removeEventListener('forceVehicleUpdate', handleForceUpdate);
    };
  }, [vehicle, startDate, endDate]);

  // Функция загрузки данных для BaseChart
  const fetchEngineData = async (vehicleData, startDateObj, endDateObj) => {
    // Используем переданные параметры или fallback на локальные состояния
    const vehicleToUse = vehicleData || vehicle;
    const startDateToUse = startDateObj || startDate;
    const endDateToUse = endDateObj || endDate;
    
    if (!vehicleToUse || !startDateToUse || !endDateToUse) {
      console.warn('EngineChart: Невозможно загрузить данные - отсутствует транспорт или даты');
      return;
    }

    // Если уже идет загрузка, отменяем предыдущий запрос
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Создаем новый AbortController для возможности отмены запроса
    abortControllerRef.current = new AbortController();
    
    try {
      // Проверяем и исправляем диапазон дат
      const { startDate: validStartDate, endDate: validEndDate } = validateDateRange(startDateToUse, endDateToUse);
      
      // Форматируем даты для API
      const formattedStartDate = validStartDate.toISOString();
      const formattedEndDate = validEndDate.toISOString();
      
      console.log(`Запрос данных о состоянии двигателя для ${vehicleToUse.name || vehicleToUse.imei}:`, {
        imei: vehicleToUse.imei,
        startDate: formattedStartDate,
        endDate: formattedEndDate
      });
      
      // Формируем URL для запроса
      const apiUrl = `/api/engine/${vehicleToUse.imei}/state?startTime=${formattedStartDate}&endTime=${formattedEndDate}`;
      
      // Получаем заголовок авторизации с токеном
      const authHeader = tokenService.getAuthHeader();
      
      // Выполняем запрос к API с добавлением токена авторизации
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...authHeader  // Добавляем заголовок авторизации
        },
        signal: abortControllerRef.current.signal
      });
      
      // Проверяем ответ
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Ошибка API: ${response.status} - ${errorText || response.statusText}`);
      }
      
      const responseData = await response.json();
      
      console.log('Получен ответ от API для состояния двигателя:', responseData);
      
      // Проверяем наличие данных в ответе
      if (responseData) {
        // Проверяем новый формат данных, где есть массив data с состоянием двигателя
        if (responseData.data && Array.isArray(responseData.data)) {
          console.log(`Получено ${responseData.data.length} точек данных о состоянии двигателя для ТС ${vehicleToUse.name || vehicleToUse.imei}`);
          
          // Обрабатываем данные
          const processedData = preprocessEngineData(responseData.data);
          
          if (processedData.values.length === 0) {
            console.warn('После обработки данных не осталось точек для графика состояния двигателя');
            return { labels: [], datasets: [] };
          } else {
            // Возвращаем данные в формате, ожидаемом BaseChart
            return {
              labels: processedData.labels,
              datasets: [{
                label: "Состояние двигателя",
                data: processedData.values.map(value => value * 1000), 
                fill: true,
                backgroundColor: "rgba(43, 192, 65, 0.3)", // Прозрачная заливка
                borderColor: "rgb(43, 192, 65)",
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 5,
                tension: 0,
                stepped: true, // Ступенчатая линия для состояния двигателя
              }]
            };
          }
        } 
        // Проверяем есть ли поле points, указывающее на старую структуру API
        else if (Array.isArray(responseData.points)) {
          console.log(`Получено ${responseData.points.length} точек для графика состояния двигателя ${vehicleToUse.name || vehicleToUse.imei}`);
          
          const processedData = preprocessEngineData(responseData.points);
          
          if (processedData.values.length === 0) {
            console.warn('После обработки данных не осталось точек для графика состояния двигателя');
            return { labels: [], datasets: [] };
          } else {
            return {
              labels: processedData.labels,
              datasets: [{
                label: "Состояние двигателя",
                data: processedData.values.map(value => value * 1000), 
                fill: true,
                backgroundColor: "rgba(43, 192, 65, 0.3)", // Прозрачная заливка
                borderColor: "rgb(43, 192, 65)",
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 5,
                tension: 0,
                stepped: true, // Ступенчатая линия для состояния двигателя
              }]
            };
          }
        } 
        // Если ответ сам является массивом
        else if (Array.isArray(responseData)) {
          console.log(`Получено ${responseData.length} точек для графика состояния двигателя ${vehicleToUse.name || vehicleToUse.imei}`);
          
          const processedData = preprocessEngineData(responseData);
          
          if (processedData.values.length === 0) {
            console.warn('После обработки данных не осталось точек для графика состояния двигателя');
            return { labels: [], datasets: [] };
          } else {
            return {
              labels: processedData.labels,
              datasets: [{
                label: "Состояние двигателя",
                data: processedData.values.map(value => value * 1000), 
                fill: true,
                backgroundColor: "rgba(43, 192, 65, 0.3)", // Прозрачная заливка
                borderColor: "rgb(43, 192, 65)",
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 5,
                tension: 0,
                stepped: true, // Ступенчатая линия для состояния двигателя
              }]
            };
          }
        } else {
          // Неизвестный формат
          console.error('API вернул неизвестный формат данных о состоянии двигателя:', responseData);
          return { labels: [], datasets: [] };
        }
      } else {
        console.error('API вернул пустой ответ');
        return { labels: [], datasets: [] };
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Запрос данных о состоянии двигателя был отменен');
        return { labels: [], datasets: [] };
      }
      
      console.error('Ошибка при загрузке данных о состоянии двигателя:', error);
      
      // Показываем уведомление об ошибке
      toast.error(`Ошибка загрузки данных о состоянии двигателя: ${error.message}`, {
        position: "top-right",
        autoClose: 5000
      });
      
      throw error; // Пробрасываем ошибку для обработки в BaseChart
    }
  };

  // Функция для предварительной обработки данных состояния двигателя
  const preprocessEngineData = (rawData) => {
    // Проверяем наличие и корректность данных
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return { values: [], labels: [] };
    }
    
    // Сортируем по времени
    const sortedData = [...rawData].sort((a, b) => {
      // Поддерживаем оба формата времени: time и _time
      const timeFieldA = a._time || a.time;
      const timeFieldB = b._time || b.time;
      
      const timeA = new Date(timeFieldA).getTime();
      const timeB = new Date(timeFieldB).getTime();
      return timeA - timeB;
    });
    
    // Выводим информацию о структуре данных
    if (sortedData.length > 0) {
      const firstItem = sortedData[0];
      console.log('Пример структуры данных точки состояния двигателя:', firstItem);
    }
    
    // Преобразуем данные в формат для графика
    const values = [];
    const labels = [];
    
    for (const item of sortedData) {
      // Получаем значение времени (поддерживаем все возможные поля: time, _time)
      const timeField = item._time || item.time;
      if (!timeField) {
        console.warn('Пропуск элемента без времени:', item);
        continue;
      }
      
      const timeValue = new Date(timeField);
      
      // Получаем значение состояния двигателя - проверяем разные варианты имени поля
      let engineValue = null;
      
      // Проверяем все возможные варианты имени поля
      if (typeof item.engineState === 'number') {
        engineValue = item.engineState;
      } 
      else if (typeof item.value === 'number') {
        engineValue = item.value;
      }
      else if (typeof item.state === 'number') {
        engineValue = item.state;
      }
      
      // Если не нашли подходящего поля, пропускаем точку
      if (engineValue === null) {
        console.warn('Пропуск элемента с неизвестным форматом данных состояния двигателя:', item);
        continue;
      }
      
      // Нормализуем значение к 0 или 1
      let normalizedValue;
      if (engineValue > 0) {
        normalizedValue = 1; // Двигатель включен
      } else {
        normalizedValue = 0; // Двигатель выключен
      }
      
      console.log(`EngineChart: Исходное значение: ${engineValue}, нормализованное: ${normalizedValue}`);
      
      // Добавляем точку в результаты
      values.push(normalizedValue);
      labels.push(timeValue);
    }
    
    return { values, labels };
  };
  
  // Форматирование подписей осей для состояния двигателя
  const formatEngineLabel = (value) => {
    if (value === 0) return 'Выключен';
    if (value === 1000) return 'Включен';
    return `${value}`;
  };

  return (
    <>
      {/* {(debugInfo || engineInfo) && (
        <div className="chart-debug-info" style={{
          padding: '8px 12px',
          margin: '0 0 10px 0',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          {debugInfo && (
            <div><strong>Отладка:</strong> {debugInfo}</div>
          )}
          {engineInfo && (
            <div style={{ marginTop: debugInfo ? '8px' : '0' }}>
              <strong>Тип двигателя:</strong> {engineInfo.type || 'Не указан'}
              {engineInfo.voltageThreshold && (
                <span style={{ marginLeft: '15px' }}>
                  <strong>Порог напряжения:</strong> {engineInfo.voltageThreshold} В
                </span>
              )}
            </div>
          )}
        </div>
      )} */}
      
      <BaseChart
        title="Состояние двигателя"
        vehicle={vehicle}
        startDate={propsStartDate || startDate}
        endDate={propsEndDate || endDate}
        color="rgb(43, 192, 65)"
        fetchData={fetchEngineData}
        formatTooltipLabel={formatEngineLabel}
        formatYAxisLabel={formatEngineLabel}
        formatXAxisLabel={formatTimeLabel}
        emptyDataMessage="Нет данных о состоянии двигателя за выбранный период"
        options={getChartOptions()}
        reportType="engine" // Добавляем тип отчета для идентификации
      />
    </>
  );
};

export default EngineChart; 